import { useState, useEffect } from "react";
import { Ava } from "../atoms/Ava.jsx";
import { Logo } from "../atoms/Logo.jsx";
import { getPendingCount } from "../services/applications.js";

/**
 * UI-1: Admin sidebar navigation.
 * Persistent left sidebar for admin console with:
 * - Persona switcher (UI-2)
 * - Navigation items with pending badge for applications
 * - Collapsible on mobile
 */

// v1.28.0 (AGCY): single "Personas" entry with tabs (Officials / Agencies)
// inside the list itself. Tried separate sidebar entries in an earlier
// iteration of this sprint but the UX felt off — each "type" was a full
// sidebar nav click, and the Agencies page rendered blank on first visit
// if the ADMIN_VIEWS set wasn't in perfect sync. Tabs inside the Personas
// list keep the type-switch local, avoid URL/view proliferation, and
// give a natural place for the empty-state CTA ("No agencies yet —
// create your first").
const NAV_ITEMS = [
  { id: "dashboard",    icon: "📊", label: "Dashboard",    url: "/" },
  { id: "reporting",    icon: "📈", label: "Reports",      url: "/admin/reporting" },
  { id: "list",         icon: "👥", label: "Personas",     url: "/admin/personas" },
  { id: "applications", icon: "📨", label: "Applications", url: "/admin/applications" },
  { id: "rewards",      icon: "🎁", label: "Rewards",      url: "/admin/rewards" },
  { id: "create",       icon: "➕", label: "New Persona",   url: "/admin/create" },
];

export function AdminSidebar({
  view,
  personas = [],
  selectedPersona,
  onSetView,
  onSelectPersona,
  onLogout,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [pendingBadge, setPendingBadge] = useState(0);

  // Fetch pending application count for badge — once on mount +
  // when navigating TO the applications view (catches approve/reject updates)
  useEffect(() => {
    getPendingCount()
      .then((data) => setPendingBadge(data.count ?? 0))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh badge when entering applications view (after approve/reject)
  useEffect(() => {
    if (view === "applications") {
      getPendingCount()
        .then((data) => setPendingBadge(data.count ?? 0))
        .catch(() => {});
    }
  }, [view === "applications"]); // eslint-disable-line react-hooks/exhaustive-deps

  const pid = selectedPersona?.id;
  const personaNav = selectedPersona
    ? [
        { id: "analytics",  icon: "📊", label: "Analytics",          url: `/admin/personas/${pid}/analytics` },
        { id: "questions",   icon: "❓", label: "Questions",          url: `/admin/personas/${pid}/questions` },
        { id: "sessions",   icon: "💬", label: "Sessions",           url: `/admin/personas/${pid}/sessions` },
        { id: "kb",         icon: "📚", label: "Knowledge Base",     url: `/admin/personas/${pid}/kb` },
        { id: "tone",       icon: "🎭", label: "Tone",               url: `/admin/personas/${pid}/tone` },
        { id: "chat",       icon: "🗣️", label: "Preview Chat",       url: `/admin/personas/${pid}/chat` },
        { id: "profile",    icon: "📋", label: "Profile",            url: `/admin/personas/${pid}` },
        { id: "statement",  icon: "📝", label: "Personal Statement", url: `/admin/personas/${pid}/statement` },
        { id: "cache",      icon: "⚡", label: "Cache",              url: `/admin/personas/${pid}/cache` },
        { id: "credits",    icon: "💰", label: "Credits",            url: `/admin/personas/${pid}/credits` },
      ]
    : [];

  const handleNav = (item) => {
    window.history.pushState({}, "", item.url);
    onSetView(item.id);
  };

  const handleSelectPersona = (p) => {
    onSelectPersona(p);
    setSwitcherOpen(false);
    window.history.pushState({}, "", `/admin/personas/${p.id}`);
    onSetView("profile");
  };

  const S = {
    sidebar: {
      width: collapsed ? 56 : 220,
      minWidth: collapsed ? 56 : 220,
      background: "#0a0f1a",
      borderRight: "1px solid #1e2d45",
      display: "flex",
      flexDirection: "column",
      transition: "width 0.2s, min-width 0.2s",
      overflow: "hidden",
      flexShrink: 0,
    },
    header: {
      padding: collapsed ? "12px 8px" : "16px 14px",
      borderBottom: "1px solid #1e2d45",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    logo: {
      width: 32,
      height: 32,
      background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      fontWeight: 900,
      color: "#fff",
      flexShrink: 0,
    },
    navItem: (active) => ({
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: collapsed ? "10px 16px" : "10px 14px",
      background: active ? "#1e3a5f" : "transparent",
      color: active ? "#60a5fa" : "#8b95a8",
      border: "none",
      borderLeft: active ? "3px solid #3b82f6" : "3px solid transparent",
      cursor: "pointer",
      fontSize: 13,
      fontFamily: "inherit",
      fontWeight: active ? 600 : 400,
      width: "100%",
      textAlign: "left",
      transition: "background 0.15s",
    }),
    section: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#475569",
      padding: "16px 14px 6px",
    },
    switcher: {
      padding: "10px 14px",
      borderBottom: "1px solid #1e2d45",
    },
    switcherBtn: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      background: "#0f1623",
      border: "1px solid #1e2d45",
      borderRadius: 8,
      cursor: "pointer",
      color: "#e2e8f0",
      fontSize: 12,
      fontFamily: "inherit",
      textAlign: "left",
    },
    dropdown: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      background: "#0f1623",
      border: "1px solid #1e2d45",
      borderRadius: 8,
      marginTop: 4,
      maxHeight: 240,
      overflowY: "auto",
      zIndex: 100,
      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
    },
    dropdownItem: (active) => ({
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      background: active ? "#1e3a5f" : "transparent",
      color: active ? "#60a5fa" : "#94a3b8",
      border: "none",
      cursor: "pointer",
      fontSize: 12,
      fontFamily: "inherit",
      width: "100%",
      textAlign: "left",
    }),
  };

  return (
    <div style={S.sidebar}>
      {/* Header with logo + collapse toggle */}
      <div style={S.header}>
        <Logo size={28} showText={false} />
        {!collapsed && (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>QuestionMe</div>
            <div style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em" }}>Admin Console</div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: 4 }}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? "▶" : "◀"}
        </button>
      </div>

      {/* UI-2: Persona switcher */}
      {!collapsed && selectedPersona && (
        <div style={S.switcher}>
          <div style={{ position: "relative" }}>
            <button style={S.switcherBtn} onClick={() => setSwitcherOpen(!switcherOpen)}>
              <Ava name={selectedPersona.name} size={24} color={selectedPersona.accent_color ||selectedPersona.accentColor} />
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {selectedPersona.name}
                </div>
              </div>
              <span style={{ color: "#475569", fontSize: 10 }}>▾</span>
            </button>

            {switcherOpen && (
              <div style={S.dropdown}>
                {personas.map((p) => (
                  <button
                    key={p.id}
                    style={S.dropdownItem(p.id === selectedPersona?.id)}
                    onClick={() => handleSelectPersona(p)}
                  >
                    <Ava name={p.name} size={20} color={p.accent_color ||p.accentColor} />
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main nav */}
      <nav style={{ flex: 1, overflowY: "auto", paddingTop: 8 }}>
        {!collapsed && <div style={S.section}>Navigation</div>}
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            style={S.navItem(view === item.id)}
            onClick={() => handleNav(item)}
            title={collapsed ? item.label : undefined}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
            {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
            {!collapsed && item.id === "applications" && pendingBadge > 0 && (
              <span style={{ background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, minWidth: 18, textAlign: "center" }}>
                {pendingBadge}
              </span>
            )}
          </button>
        ))}

        {/* Persona-specific nav (when a persona is selected) */}
        {selectedPersona && personaNav.length > 0 && (
          <>
            {!collapsed && (
              <div style={{ ...S.section, marginTop: 8 }}>
                {selectedPersona.name.split(" ").pop()}
              </div>
            )}
            {personaNav.map((item) => (
              <button
                key={item.id}
                style={S.navItem(view === item.id)}
                onClick={() => handleNav(item)}
                title={collapsed ? item.label : undefined}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span>{item.label}</span>}
              </button>
            ))}
          </>
        )}
      </nav>

      {/* Footer — logout */}
      <div style={{ borderTop: "1px solid #1e2d45", padding: "10px 14px" }}>
        <button
          onClick={onLogout}
          style={S.navItem(false)}
        >
          <span style={{ fontSize: 16 }}>🚪</span>
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </div>
  );
}
